import React, { useState, useMemo } from 'react';
import {
  Database,
  AlertCircle,
  User,
  Film,
  Music,
  Clock,
  Search,
  CheckCircle2,
  XCircle,
  FileJson,
  X,
  Table,
  Activity,
  Play
} from 'lucide-react';
import {
  InspectStats,
  ClipHealth,
  ClipInspectorData,
  fetchClipInspector,
  claimClips,
  claimAllOrphaned
} from '../../services/inspectService';

type FilterType = 'all' | 'orphaned' | 'withStems' | 'noStems';
type SortField = 'created_at' | 'user_name' | 'title';
type SortDir = 'asc' | 'desc';
type InspectorTab = 'd1' | 'user' | 'r2json' | 'health' | 'preview';

interface ClipsTabProps {
  stats: InspectStats | null;
  allClips: ClipHealth[];
  isLoading: boolean;
  onRefreshData: () => void;
}

export const ClipsTab: React.FC<ClipsTabProps> = ({
  stats,
  allClips,
  isLoading,
  onRefreshData
}) => {
  const [filter, setFilter] = useState<FilterType>('all');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const [selectedClipId, setSelectedClipId] = useState<string | null>(null);
  const [inspectorData, setInspectorData] = useState<ClipInspectorData | null>(null);
  const [isLoadingInspector, setIsLoadingInspector] = useState(false);
  const [inspectorTab, setInspectorTab] = useState<InspectorTab>('d1');

  const [claimUserName, setClaimUserName] = useState('Dan');
  const [isClaiming, setIsClaiming] = useState(false);
  const [claimResult, setClaimResult] = useState<{ claimed: number; total: number } | null>(null);

  // Fast client-side search, filtering, and sorting (0 D1 row reads while filtering)
  const clips = useMemo(() => {
    let result = [...allClips];

    // Status filter
    if (filter === 'orphaned') {
      result = result.filter(c => !c.user_name || c.user_name.trim() === '');
    } else if (filter === 'withStems') {
      result = result.filter(c => !!c.audio_url || !!c.hasStems);
    } else if (filter === 'noStems') {
      result = result.filter(c => !c.audio_url && !c.hasStems);
    }

    // Search query filter (instantaneous in-memory filtering)
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter(c =>
        (c.title || '').toLowerCase().includes(q) ||
        (c.id || '').toLowerCase().includes(q) ||
        (c.user_name || '').toLowerCase().includes(q)
      );
    }

    // Sort
    result.sort((a, b) => {
      let valA: any = a[sortField];
      let valB: any = b[sortField];
      if (sortField === 'created_at') {
        valA = a.created_at || 0;
        valB = b.created_at || 0;
      } else {
        valA = (valA || '').toString().toLowerCase();
        valB = (valB || '').toString().toLowerCase();
      }
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [allClips, filter, search, sortField, sortDir]);

  const handleSelectClip = async (clipId: string) => {
    setSelectedClipId(clipId);
    setIsLoadingInspector(true);
    setInspectorTab('d1');
    try {
      const data = await fetchClipInspector(clipId);
      setInspectorData(data);
    } finally {
      setIsLoadingInspector(false);
    }
  };

  const handleCloseInspector = () => {
    setSelectedClipId(null);
    setInspectorData(null);
  };

  const handleClaimOrphaned = async () => {
    if (!claimUserName.trim()) return;
    setIsClaiming(true);
    setClaimResult(null);
    try {
      const result = await claimAllOrphaned(claimUserName.trim());
      setClaimResult(result);
      if (result.claimed > 0) {
        onRefreshData();
      }
    } finally {
      setIsClaiming(false);
    }
  };

  const formatTimestamp = (ts: number | null): string => {
    if (!ts) return 'N/A';
    return new Date(ts).toLocaleString();
  };

  const formatDuration = (ms: number): string => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  return (
    <div>
      {/* Stats Cards */}
      {stats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          <StatCard
            icon={<Database size={20} />}
            label="Total Clips"
            value={stats.totalClips}
            color="var(--accent-purple)"
          />
          <StatCard
            icon={<AlertCircle size={20} />}
            label="Orphaned Clips"
            value={stats.orphanedClips}
            color="var(--accent-amber)"
            highlight={stats.orphanedClips > 0}
          />
          <StatCard
            icon={<User size={20} />}
            label="Performers"
            value={Object.keys(stats.clipsByUser).length}
            color="var(--accent-cyan)"
          />
          <StatCard
            icon={<Film size={20} />}
            label="Entries"
            value={stats.totalEntries}
            color="var(--accent-emerald)"
          />
          <StatCard
            icon={<Music size={20} />}
            label="Takes"
            value={stats.totalTakes}
            color="var(--accent-coral)"
          />
          <StatCard
            icon={<Clock size={20} />}
            label="Library Age"
            value={stats.oldestClip && stats.newestClip
              ? formatDuration(stats.newestClip - stats.oldestClip)
              : 'N/A'}
            color="var(--text-muted)"
          />
        </div>
      )}

      {/* Action Bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        marginBottom: '1.25rem',
        flexWrap: 'wrap'
      }}>
        {/* Filter Pills */}
        <div style={{ display: 'flex', gap: '8px' }}>
          {(['all', 'orphaned', 'withStems', 'noStems'] as FilterType[]).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              style={{
                padding: '7px 14px',
                borderRadius: 'var(--radius-full)',
                border: filter === f ? 'none' : '1px solid var(--border-glass)',
                background: filter === f
                  ? 'linear-gradient(135deg, var(--accent-purple) 0%, var(--accent-coral) 100%)'
                  : 'var(--bg-glass)',
                color: 'var(--text-primary)',
                cursor: 'pointer',
                fontWeight: filter === f ? 700 : 500,
                fontSize: '0.82rem',
                fontFamily: 'var(--font-body)',
                textTransform: 'capitalize'
              }}
            >
              {f === 'all' ? 'All Clips' : f === 'orphaned' ? 'Orphaned' : f === 'withStems' ? 'With Stems' : 'No Stems'}
            </button>
          ))}
        </div>

        {/* Search */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '7px 14px',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border-glass)',
          background: 'var(--bg-glass)',
          flex: '1 1 260px',
          maxWidth: '380px'
        }}>
          <Search size={15} color="var(--text-muted)" />
          <input
            type="text"
            placeholder="Search by title, ID, or performer..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              border: 'none',
              background: 'transparent',
              color: 'var(--text-primary)',
              outline: 'none',
              fontFamily: 'var(--font-body)',
              fontSize: '0.85rem',
              width: '100%'
            }}
          />
        </div>

        {/* Claim Orphaned Controls */}
        {stats && stats.orphanedClips > 0 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginLeft: 'auto'
          }}>
            <input
              type="text"
              placeholder="Username"
              value={claimUserName}
              onChange={(e) => setClaimUserName(e.target.value)}
              style={{
                padding: '7px 12px',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border-glass)',
                background: 'var(--bg-glass)',
                color: 'var(--text-primary)',
                fontFamily: 'var(--font-mono)',
                fontSize: '0.85rem',
                width: '110px'
              }}
            />
            <button
              onClick={handleClaimOrphaned}
              disabled={isClaiming || !claimUserName.trim()}
              style={{
                padding: '7px 14px',
                borderRadius: 'var(--radius-md)',
                border: 'none',
                background: 'linear-gradient(135deg, var(--accent-emerald) 0%, var(--accent-cyan) 100%)',
                color: 'white',
                cursor: isClaiming ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                fontSize: '0.82rem',
                fontFamily: 'var(--font-body)',
                opacity: isClaiming ? 0.6 : 1
              }}
            >
              {isClaiming ? 'Claiming...' : `Claim All (${stats.orphanedClips})`}
            </button>
          </div>
        )}
      </div>

      {/* Claim Result Banner */}
      {claimResult && (
        <div style={{
          padding: '10px 14px',
          borderRadius: 'var(--radius-md)',
          background: claimResult.claimed > 0 ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
          border: `1px solid ${claimResult.claimed > 0 ? 'var(--accent-emerald)' : 'var(--accent-coral)'}`,
          marginBottom: '1rem',
          color: 'var(--text-primary)',
          fontSize: '0.85rem'
        }}>
          {claimResult.claimed > 0
            ? `Successfully claimed ${claimResult.claimed} of ${claimResult.total} orphaned clips.`
            : `No clips were claimed. (${claimResult.total} orphaned clips)`}
        </div>
      )}

      {/* Sort row */}
      {clips.length > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '0.85rem',
          color: 'var(--text-muted)',
          fontSize: '0.78rem'
        }}>
          <span>Sort by:</span>
          {(['created_at', 'user_name', 'title'] as SortField[]).map((field) => (
            <button
              key={field}
              onClick={() => {
                if (sortField === field) {
                  setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
                } else {
                  setSortField(field);
                  setSortDir('desc');
                }
              }}
              style={{
                padding: '3px 9px',
                borderRadius: 'var(--radius-sm)',
                border: sortField === field ? '1px solid var(--accent-purple)' : '1px solid var(--border-glass)',
                background: sortField === field ? 'rgba(139, 92, 246, 0.2)' : 'transparent',
                color: sortField === field ? 'var(--accent-purple)' : 'var(--text-muted)',
                cursor: 'pointer',
                fontSize: '0.75rem',
                fontWeight: sortField === field ? 600 : 400
              }}
            >
              {field === 'created_at' ? 'Date' : field === 'user_name' ? 'Performer' : 'Title'}
              {sortField === field && (sortDir === 'asc' ? ' ↑' : ' ↓')}
            </button>
          ))}
          <span style={{ marginLeft: 'auto' }}>
            Showing <strong>{clips.length}</strong> {clips.length === 1 ? 'clip' : 'clips'}
          </span>
        </div>
      )}

      {/* Clips Table */}
      <div style={{
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border-glass)',
        background: 'var(--bg-card)',
        overflow: 'hidden'
      }}>
        {isLoading ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
            <p>Loading clip inventory...</p>
          </div>
        ) : clips.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
            <FileJson size={24} style={{ marginBottom: '8px' }} />
            <p>No clips match the current filters</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ background: 'rgba(0,0,0,0.25)' }}>
                  <th style={{ ...thStyle, width: '60px' }}>Thumb</th>
                  <th style={thStyle}>ID</th>
                  <th style={thStyle}>Title</th>
                  <th style={thStyle}>Performer</th>
                  <th style={thStyle}>Created</th>
                  <th style={thStyle}>Poster</th>
                  <th style={thStyle}>Audio</th>
                  <th style={thStyle}>Stems</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {clips.map((clip, idx) => (
                  <tr
                    key={clip.id}
                    onClick={() => handleSelectClip(clip.id)}
                    style={{
                      cursor: 'pointer',
                      borderTop: idx > 0 ? '1px solid var(--border-glass)' : 'none',
                      background: selectedClipId === clip.id ? 'rgba(139, 92, 246, 0.12)' : 'transparent',
                      transition: 'background var(--transition-fast)'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedClipId !== clip.id) {
                        e.currentTarget.style.background = 'var(--bg-card-hover)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedClipId !== clip.id) {
                        e.currentTarget.style.background = 'transparent';
                      }
                    }}
                  >
                    <td style={{ ...tdStyle, width: '60px' }}>
                      <img
                        src={clip.posterUrl || (clip.parent_clip_id ? `https://img.youtube.com/vi/${clip.parent_clip_id}/hqdefault.jpg` : `/api/media/clips/${clip.id}.jpg`)}
                        alt={clip.title}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                        style={{
                          width: '46px',
                          height: '30px',
                          objectFit: 'cover',
                          borderRadius: 'var(--radius-sm)',
                          background: 'var(--bg-glass)'
                        }}
                      />
                    </td>
                    <td style={tdStyle}>
                      <code style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: '0.74rem',
                        color: 'var(--accent-purple)'
                      }}>
                        {clip.id.length > 18 ? clip.id.slice(0, 18) + '…' : clip.id}
                      </code>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                        {clip.title.length > 28 ? clip.title.slice(0, 28) + '…' : clip.title}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      {clip.user_name ? (
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: 'var(--radius-full)',
                          background: 'rgba(139, 92, 246, 0.18)',
                          color: 'var(--accent-purple)',
                          fontSize: '0.74rem',
                          fontWeight: 600
                        }}>
                          {clip.user_name}
                        </span>
                      ) : (
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: 'var(--radius-full)',
                          background: 'rgba(245, 158, 11, 0.2)',
                          color: 'var(--accent-amber)',
                          fontSize: '0.74rem',
                          fontWeight: 600
                        }}>
                          orphaned
                        </span>
                      )}
                    </td>
                    <td style={{ ...tdStyle, color: 'var(--text-muted)', fontSize: '0.78rem' }}>
                      {formatTimestamp(clip.created_at)}
                    </td>
                    <td style={tdStyle}>
                      <HealthIcon value={clip.hasPoster} />
                    </td>
                    <td style={tdStyle}>
                      <HealthIcon value={clip.hasAudio} />
                    </td>
                    <td style={tdStyle}>
                      <HealthIcon value={clip.hasStems} />
                    </td>
                    <td style={tdStyle}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectClip(clip.id);
                        }}
                        style={{
                          padding: '4px 8px',
                          borderRadius: 'var(--radius-sm)',
                          border: '1px solid var(--border-glass)',
                          background: 'transparent',
                          color: 'var(--text-secondary)',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontSize: '0.74rem'
                        }}
                      >
                        <FileJson size={12} />
                        Inspect
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Inspector Side Drawer */}
      {selectedClipId && (
        <div style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: '620px',
          maxWidth: '92vw',
          height: '100vh',
          background: 'var(--bg-deep)',
          borderLeft: '1px solid var(--border-glass)',
          boxShadow: '-10px 0 45px rgba(0,0,0,0.6)',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          animation: 'slideIn 0.2s ease-out'
        }}>
          {/* Header */}
          <div style={{
            padding: '1.25rem',
            borderBottom: '1px solid var(--border-glass)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '1rem'
          }}>
            {inspectorData?.d1Record && (
              <img
                src={inspectorData.d1Record.poster_url || (inspectorData.d1Record.parent_clip_id ? `https://img.youtube.com/vi/${inspectorData.d1Record.parent_clip_id}/hqdefault.jpg` : `/api/media/clips/${selectedClipId}.jpg`)}
                alt="Thumbnail"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                style={{
                  width: '90px',
                  height: '60px',
                  objectFit: 'cover',
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--bg-glass)',
                  flexShrink: 0
                }}
              />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2 style={{
                fontSize: '1.15rem',
                fontWeight: 700,
                color: 'var(--text-primary)',
                fontFamily: 'var(--font-display)',
                marginBottom: '3px'
              }}>
                {inspectorData?.d1Record?.title || selectedClipId}
              </h2>
              <p style={{
                fontSize: '0.72rem',
                color: 'var(--text-muted)',
                fontFamily: 'var(--font-mono)',
                wordBreak: 'break-all'
              }}>
                {selectedClipId}
              </p>
            </div>
            <button
              onClick={handleCloseInspector}
              style={{
                padding: '6px',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border-glass)',
                background: 'transparent',
                color: 'var(--text-muted)',
                cursor: 'pointer'
              }}
            >
              <X size={18} />
            </button>
          </div>

          {/* Drawer Tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid var(--border-glass)' }}>
            {([
              { key: 'd1', label: 'D1 Row', icon: <Table size={13} /> },
              { key: 'user', label: 'Ownership', icon: <User size={13} /> },
              { key: 'r2json', label: 'R2 JSON', icon: <FileJson size={13} /> },
              { key: 'health', label: 'Stems & Media', icon: <Activity size={13} /> },
              { key: 'preview', label: 'Preview', icon: <Play size={13} /> }
            ] as { key: InspectorTab; label: string; icon: React.ReactNode }[]).map((tab) => (
              <button
                key={tab.key}
                onClick={() => setInspectorTab(tab.key)}
                style={{
                  flex: 1,
                  padding: '10px 4px',
                  border: 'none',
                  background: inspectorTab === tab.key ? 'rgba(139, 92, 246, 0.15)' : 'transparent',
                  color: inspectorTab === tab.key ? 'var(--accent-purple)' : 'var(--text-muted)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '5px',
                  fontSize: '0.8rem',
                  fontWeight: inspectorTab === tab.key ? 700 : 500,
                  borderBottom: inspectorTab === tab.key ? '2px solid var(--accent-purple)' : 'none'
                }}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>

          {/* Drawer Content */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem' }}>
            {isLoadingInspector ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                <p>Loading clip details...</p>
              </div>
            ) : inspectorData ? (
              <>
                {inspectorTab === 'd1' && (
                  <div style={{ display: 'grid', gap: '6px' }}>
                    {Object.entries(inspectorData.d1Record).map(([key, value]) => (
                      <div
                        key={key}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '130px 1fr',
                          gap: '10px',
                          padding: '7px 10px',
                          borderRadius: 'var(--radius-sm)',
                          background: 'var(--bg-glass)',
                          fontSize: '0.82rem'
                        }}
                      >
                        <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: '0.75rem' }}>
                          {key}
                        </span>
                        <span style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-mono)', fontSize: '0.78rem', wordBreak: 'break-all' }}>
                          {value === null ? (
                            <span style={{ color: 'var(--accent-amber)' }}>null</span>
                          ) : typeof value === 'object' ? (
                            JSON.stringify(value)
                          ) : String(value)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {inspectorTab === 'user' && (
                  <div style={{ display: 'grid', gap: '12px' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '1rem',
                      borderRadius: 'var(--radius-md)',
                      background: inspectorData.d1Record?.user_name
                        ? 'rgba(16, 185, 129, 0.1)'
                        : 'rgba(245, 158, 11, 0.1)',
                      border: `1px solid ${inspectorData.d1Record?.user_name ? 'var(--accent-emerald)' : 'var(--accent-amber)'}`
                    }}>
                      <User size={22} color={inspectorData.d1Record?.user_name ? 'var(--accent-emerald)' : 'var(--accent-amber)'} />
                      <div>
                        <div style={{ color: 'var(--text-primary)', fontWeight: 700 }}>
                          {inspectorData.d1Record?.user_name || 'Orphaned (unassigned)'}
                        </div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>
                          {inspectorData.d1Record?.user_name
                            ? 'Claimed performer profile'
                            : 'No performer assigned'}
                        </div>
                      </div>
                    </div>

                    {!inspectorData.d1Record?.user_name && (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <input
                          type="text"
                          placeholder="Performer name"
                          value={claimUserName}
                          onChange={(e) => setClaimUserName(e.target.value)}
                          style={{
                            flex: 1,
                            padding: '8px 12px',
                            borderRadius: 'var(--radius-sm)',
                            border: '1px solid var(--border-glass)',
                            background: 'var(--bg-glass)',
                            color: '#fff',
                            fontFamily: 'var(--font-mono)',
                            fontSize: '0.85rem'
                          }}
                        />
                        <button
                          onClick={async () => {
                            if (!claimUserName.trim()) return;
                            const res = await claimClips(claimUserName.trim(), [selectedClipId]);
                            if (res.claimed > 0) {
                              handleSelectClip(selectedClipId);
                              onRefreshData();
                            }
                          }}
                          style={{
                            padding: '8px 14px',
                            borderRadius: 'var(--radius-md)',
                            border: 'none',
                            background: 'linear-gradient(135deg, var(--accent-emerald) 0%, var(--accent-cyan) 100%)',
                            color: 'white',
                            cursor: 'pointer',
                            fontWeight: 600,
                            fontSize: '0.82rem'
                          }}
                        >
                          Claim
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {inspectorTab === 'r2json' && (
                  <div>
                    {inspectorData.r2Json ? (
                      <pre style={{
                        padding: '1rem',
                        borderRadius: 'var(--radius-md)',
                        background: 'var(--bg-cosmic)',
                        border: '1px solid var(--border-glass)',
                        overflow: 'auto',
                        fontSize: '0.72rem',
                        fontFamily: 'var(--font-mono)',
                        color: 'var(--text-primary)',
                        maxHeight: '65vh'
                      }}>
                        {JSON.stringify(inspectorData.r2Json, null, 2)}
                      </pre>
                    ) : (
                      <div style={{ padding: '1rem', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                        No R2 JSON metadata found (`clips/{selectedClipId}.json`).
                      </div>
                    )}
                  </div>
                )}

                {inspectorTab === 'health' && (
                  <div style={{ display: 'grid', gap: '10px' }}>
                    {[
                      { key: 'mp4Exists', label: 'MP4 Video Asset', path: `clips/${selectedClipId}.mp4` },
                      { key: 'jpgExists', label: 'Poster JPG', path: `clips/${selectedClipId}.jpg` },
                      { key: 'vocalsExists', label: 'Demucs Vocals Stem', path: `stems/${selectedClipId}/vocals.mp3` },
                      { key: 'bgmExists', label: 'Demucs BGM Stem', path: `stems/${selectedClipId}/bgm.mp3` }
                    ].map(({ key, label, path }) => {
                      const exists = inspectorData.r2Health[key as keyof typeof inspectorData.r2Health];
                      return (
                        <div
                          key={key}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '10px 14px',
                            borderRadius: 'var(--radius-md)',
                            background: 'var(--bg-glass)',
                            border: `1px solid ${exists ? 'rgba(16, 185, 129, 0.4)' : 'rgba(239, 68, 68, 0.4)'}`
                          }}
                        >
                          <div>
                            <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '0.85rem' }}>
                              {label}
                            </div>
                            <code style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                              {path}
                            </code>
                          </div>
                          {exists ? (
                            <CheckCircle2 size={20} color="var(--accent-emerald)" />
                          ) : (
                            <XCircle size={20} color="var(--accent-coral)" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {inspectorTab === 'preview' && (
                  <div>
                    <video
                      controls
                      src={`/api/media/clips/${selectedClipId}.mp4`}
                      style={{
                        width: '100%',
                        maxHeight: '360px',
                        borderRadius: 'var(--radius-md)',
                        background: '#000',
                        marginBottom: '1rem'
                      }}
                    />
                    <div style={{ display: 'grid', gap: '8px' }}>
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Stems Audio Preview:</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <div>
                          <span style={{ fontSize: '0.74rem', color: 'var(--text-secondary)' }}>Vocals: </span>
                          <audio controls src={`/api/media/stems/${selectedClipId}/vocals.mp3`} style={{ width: '100%', height: '36px' }} />
                        </div>
                        <div>
                          <span style={{ fontSize: '0.74rem', color: 'var(--text-secondary)' }}>BGM / Score: </span>
                          <audio controls src={`/api/media/stems/${selectedClipId}/bgm.mp3`} style={{ width: '100%', height: '36px' }} />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

const thStyle: React.CSSProperties = {
  padding: '10px 14px',
  textAlign: 'left',
  color: 'var(--text-muted)',
  fontWeight: 600,
  fontSize: '0.74rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em'
};

const tdStyle: React.CSSProperties = {
  padding: '10px 14px'
};

const StatCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: number | string;
  color: string;
  highlight?: boolean;
}> = ({ icon, label, value, color, highlight }) => (
  <div style={{
    padding: '1rem 1.25rem',
    borderRadius: 'var(--radius-lg)',
    background: highlight ? 'rgba(245, 158, 11, 0.1)' : 'var(--bg-card)',
    border: `1px solid ${highlight ? 'var(--accent-amber)' : 'var(--border-glass)'}`,
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  }}>
    <div style={{
      padding: '9px',
      borderRadius: 'var(--radius-md)',
      background: `${color}20`,
      color: color
    }}>
      {icon}
    </div>
    <div>
      <div style={{
        fontSize: '1.4rem',
        fontWeight: 800,
        color: highlight ? 'var(--accent-amber)' : 'var(--text-primary)',
        fontFamily: 'var(--font-display)',
        lineHeight: 1.1
      }}>
        {value}
      </div>
      <div style={{
        fontSize: '0.72rem',
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginTop: '2px'
      }}>
        {label}
      </div>
    </div>
  </div>
);

const HealthIcon: React.FC<{ value: boolean }> = ({ value }) => {
  if (value) {
    return <CheckCircle2 size={16} color="var(--accent-emerald)" />;
  }
  return <XCircle size={16} color="var(--accent-coral)" />;
};
